import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { LoadError } from '@/features/clients/LoadError'
import { useClientDashboard, useClientHelpDesk, useClientLinearIssues } from '@/queries/clientDashboards'
import { OPEN_HUB_STATUSES, hubStatusLabel, hubStatusVariant } from '@/lib/linearStatus'
import { useTrackerTickets } from '@/queries/tracker'
import { areaName } from '@/lib/productAreas'
import { trackerStatusLabels, trackerStatusVariants } from '@/lib/status'
import { formatTicketRef } from '@/lib/ticketRef'
import { typeName } from '@/lib/ticketTypes'
import type { HelpDeskHealth } from '@/types/clientDashboard'
import type { ClientLinearIssues, HubStatus } from '@/types/linearClient'
import type { TrackerStatus, TrackerTicket } from '@/types/tracker'
import { AreaBars, DashCard, Legend, SegmentBar, sectionLabelClass } from './dashboardParts'
import { formatMinutes } from './HelpDeskHealthView'
import { clientTabPath, openTrackerParams } from './tabs'
import { typeColour } from './typeColours'

const OPEN_TRACKER_STATUSES: { status: TrackerStatus; help: string }[] = [
  { status: 'new', help: 'Not looked at yet' },
  { status: 'needs_info', help: 'We need more from them' },
  { status: 'accepted', help: 'Agreed, being worked on' },
  { status: 'on_hold', help: 'Paused for now' },
  { status: 'proposed_closed', help: 'Done, waiting for them to confirm' },
]
const MAX_AREAS = 6

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className={sectionLabelClass}>{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-etn-text">{value}</div>
    </div>
  )
}

const OVERVIEW_HUB_STATUSES: HubStatus[] = [...OPEN_HUB_STATUSES, 'released']

function LinearTicketsCard({ companyId, clientName, linear }: { companyId: string; clientName: string; linear: ClientLinearIssues }) {
  const tabPath = clientTabPath(companyId, 'linear-tickets')
  const countOf = (status: HubStatus) =>
    linear.issues.filter(issue => issue.status === status && (status !== 'released' || issue.recentRelease)).length
  return (
    <DashCard
      title="Linear Tickets"
      subtitle="Where their requests stand in Linear"
      testId="overview-card-linear-tickets"
      footLink={linear.linked ? { to: tabPath, label: 'Open Linear Tickets' } : undefined}
    >
      {linear.linked ? (
        OVERVIEW_HUB_STATUSES.map(status => (
          <Link
            key={status}
            to={tabPath as never}
            search={{ status } as never}
            className="flex items-center gap-3 border-t border-etn-border-row px-[18px] py-3 first:border-t-0 hover:bg-etn-row-hover"
          >
            <Badge variant={hubStatusVariant[status]}>{status === 'released' ? 'Released, last 90 days' : hubStatusLabel(status, clientName)}</Badge>
            <span className="flex-1" />
            <span className="text-lg font-semibold tabular-nums text-etn-text">{countOf(status)}</span>
          </Link>
        ))
      ) : (
        <p className="px-[18px] py-6 text-center text-[13px] text-etn-text-soft">
          Not linked to a Linear customer yet. Link it on the{' '}
          <Link to={'/admin/settings' as never} className="font-medium text-etn-accent-text-active hover:underline">
            Settings page
          </Link>
          .
        </p>
      )}
    </DashCard>
  )
}

function ClientTrackerCard({ companyId, items }: { companyId: string; items: TrackerTicket[] }) {
  const tabPath = clientTabPath(companyId, 'client-tracker')
  return (
    <DashCard
      title="Client Tracker"
      subtitle="Open items by status, as the client sees them"
      testId="overview-card-client-tracker"
      footLink={{ to: tabPath, label: 'Open Client Tracker' }}
    >
      {OPEN_TRACKER_STATUSES.map(({ status, help }) => (
        <Link
          key={status}
          to={tabPath as never}
          className="flex items-center gap-3 border-t border-etn-border-row px-[18px] py-3 first:border-t-0 hover:bg-etn-row-hover"
        >
          <Badge variant={trackerStatusVariants[status]}>{trackerStatusLabels[status]}</Badge>
          <span className="flex-1 text-[13px] text-etn-text-soft">{help}</span>
          <span className="text-lg font-semibold tabular-nums text-etn-text">{items.filter(item => item.tracker.status === status).length}</span>
        </Link>
      ))}
    </DashCard>
  )
}

function WaitingCard({ items }: { items: TrackerTicket[] }) {
  const waiting = items.filter(item => item.tracker.waitingOn === 'client')
  const clientName = items.find(item => item.companyName)?.companyName ?? 'the client'
  return (
    <DashCard title={`Waiting on ${clientName}`} subtitle="Tracked items they need to act on" testId="overview-card-waiting">
      {waiting.length ? (
        waiting.map(item => (
          <Link key={item._id} to={`/tracker/${item._id}` as never} className="block border-t border-etn-border-row px-[18px] py-3 first:border-t-0 hover:bg-etn-row-hover">
            <span className="block font-medium text-etn-text">{item.tracker.nextAction || item.subject}</span>
            {item.tracker.nextAction && (
              <span className="mt-0.5 block text-[12.5px] text-etn-text-label">
                Holds up {formatTicketRef(item)} {item.subject}
              </span>
            )}
          </Link>
        ))
      ) : (
        <p className="px-[18px] py-6 text-center text-[13px] text-etn-text-soft">Nothing waiting on them</p>
      )}
    </DashCard>
  )
}

function HelpDeskHealthCard({ companyId, figures }: { companyId: string; figures: HelpDeskHealth }) {
  const segments = [...figures.byType]
    .sort((left, right) => right.count - left.count)
    .map(row => ({ key: row.type ?? 'none', label: row.type ? typeName(row.type) : 'No type', count: row.count, colour: typeColour(row.type) }))
  return (
    <DashCard
      title="HelpDesk Health"
      subtitle="Last 12 months"
      testId="overview-card-helpdesk-health"
      footLink={{ to: clientTabPath(companyId, 'helpdesk-health'), label: 'Open HelpDesk Health' }}
    >
      <div className="space-y-4 px-[18px] py-3.5">
        <div className="grid grid-cols-4 gap-3">
          <Stat label="Raised" value={figures.totals.raised} />
          <Stat label="Open" value={figures.totals.open} />
          <Stat label="First reply" value={formatMinutes(figures.medianFirstReplyMinutes)} />
          <Stat label="To complete" value={formatMinutes(figures.medianCompleteMinutes)} />
        </div>
        {figures.totals.raised > 0 && (
          <div className="space-y-2">
            <SegmentBar segments={segments} label="Tickets by type" />
            <Legend items={segments.map(segment => ({ key: segment.key, label: `${segment.label} ${segment.count}`, colour: segment.colour }))} />
          </div>
        )}
      </div>
    </DashCard>
  )
}

function HelpDeskAreasCard({ companyId, figures }: { companyId: string; figures: HelpDeskHealth }) {
  return (
    <DashCard
      title="What they ask the help desk about"
      subtitle="Darker part is information requests"
      testId="overview-card-help-desk-areas"
      footLink={{ to: clientTabPath(companyId, 'helpdesk-health'), label: 'HelpDesk Health' }}
    >
      <div className="px-[18px] py-3.5">
        {figures.byArea.length ? (
          <AreaBars areas={figures.byArea} labelOf={areaName} limit={MAX_AREAS} />
        ) : (
          <p className="text-center text-[13px] text-etn-text-soft">Nothing raised in the last 12 months</p>
        )}
      </div>
    </DashCard>
  )
}

export function OverviewView({ companyId }: { companyId: string }) {
  const helpDesk = useClientHelpDesk(companyId, '12m')
  const tracker = useTrackerTickets(openTrackerParams(companyId), undefined, { enabled: true })
  const linearIssues = useClientLinearIssues(companyId)
  const figures = helpDesk.data?.data
  const items = tracker.data?.data
  const linear = linearIssues.data?.data
  const clientName = useClientDashboard(companyId).data?.data.companyName ?? 'the client'
  const failed = (query: { isError: boolean; refetch: () => unknown }, title: string) =>
    query.isError ? <LoadError title={title} onRetry={() => query.refetch()} /> : null

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <div className="space-y-4">
        {linear ? <LinearTicketsCard companyId={companyId} clientName={clientName} linear={linear} /> : failed(linearIssues, "Linear Tickets didn't load")}
        {items ? <ClientTrackerCard companyId={companyId} items={items} /> : failed(tracker, "Client Tracker didn't load")}
        {figures ? <HelpDeskHealthCard companyId={companyId} figures={figures} /> : failed(helpDesk, "HelpDesk Health didn't load")}
      </div>
      <div className="space-y-4">
        {items && <WaitingCard items={items} />}
        {figures && <HelpDeskAreasCard companyId={companyId} figures={figures} />}
      </div>
    </div>
  )
}
